import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackPageView, trackEvent, attachUserProps } from '@/analytics/analytics';
import { Menu, ChevronsLeft } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import TeacherSidebar from '@/components/TeacherSidebar';
import AdminSidebar from '@/components/AdminSidebar';
import GuardianSidebar from '@/components/GuardianSidebar';
import MobileBottomNav from '@/components/MobileBottomNav';
import TeacherHeader from '@/components/TeacherHeader';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { cn } from '@/lib/utils';
import { TeacherProvider } from '@/context/TeacherContext';
import { getPortalType, clearAuthTokens, clearAllSessions } from '@/utils/auth';

export default function Layout({ children, activeSection = 'curiosity' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const portal = getPortalType(location.pathname || '');
  const lastNavigationRef = useRef({ path: '', time: 0 });
  
  // ... existing code ...
  
  // Auto-detect activeSection from URL path for teacher, guardian, and admin portals
  const getActiveSectionFromPath = () => {
    if (portal === 'teacher') {
      const pathname = location.pathname;
      if (pathname.includes('/dashboard')) return 'dashboard';
      if (pathname.includes('/my-classes')) return 'my-classes';
      if (pathname.includes('/ai-tools')) return 'ai-tools';
      if (pathname.includes('/notes')) return 'notes';
      if (pathname.includes('/training')) return 'training';
      if (pathname.includes('/parent-updates')) return 'parent-updates';
      if (pathname.includes('/community')) return 'community';
      if (pathname.includes('/about')) return 'about';
      if (pathname.includes('/profile')) return 'profile';
    } else if (portal === 'guardian') {
      const pathname = location.pathname;
      if (pathname.includes('/community')) return 'community';
      if (pathname.includes('/about')) return 'about';
    } else if (portal === 'admin') {
      const pathname = location.pathname;
      if (pathname.includes('/dashboard')) return 'dashboard';
      if (pathname.includes('/students')) return 'students';
      if (pathname.includes('/teachers')) return 'teachers';
      if (pathname.includes('/classes')) return 'classes';
      if (pathname.includes('/lessons')) return 'lessons';
      if (pathname.includes('/assessments')) return 'assessments';
      if (pathname.includes('/analytics/school')) return 'school-analytics';
      if (pathname.includes('/analytics/students')) return 'student-analytics';
      if (pathname.includes('/analytics/teachers')) return 'teacher-analytics';
      if (pathname.includes('/settings')) return 'settings';
      if (pathname.includes('/about')) return 'about';
      if (pathname.includes('/community')) return 'community';
      if (pathname.includes('/profile')) return 'profile';
    } else if (portal === 'student') {
      const pathname = location.pathname;
      if (pathname.includes('/quests')) return 'quests';
      if (pathname.includes('/chat')) return 'chat';
      if (pathname.includes('/learn') && !pathname.includes('/curiosity')) return 'learn';
      if (pathname.includes('/curiosity')) return 'curiosity';
      if (pathname.includes('/community')) return 'community';
      if (pathname.includes('/achievements/leaderboard')) return 'leaderboard';
      if (pathname.includes('/achievements/shop')) return 'shop';
      if (pathname.includes('/profile')) return 'profile';
      if (pathname.includes('/settings')) return 'settings';
      if (pathname.includes('/about')) return 'about';
      // Default to learn for student portal
      return 'learn';
    }
    return activeSection;
  };
  
  const currentActiveSection = getActiveSectionFromPath();
  
  // Teacher theme state management
  const [teacherTheme, setTeacherTheme] = useState(() => {
    return localStorage.getItem('teacherTheme') || 'light';
  });

  const [isTeacherSidebarOpen, setIsTeacherSidebarOpen] = useState(false);

  // Apply teacher theme to document root
  useEffect(() => {
    if (portal === 'teacher') {
      const root = document.documentElement;
      root.classList.remove('dark', 'teacher-theme', 'teacher-theme-dark');
      if (teacherTheme === 'dark') {
        root.classList.add('teacher-theme-dark');
      } else {
        root.classList.add('teacher-theme');
      }
    }
  }, [portal, teacherTheme]);

  const handleToggleTheme = () => {
    const newTheme = teacherTheme === 'light' ? 'dark' : 'light';
    setTeacherTheme(newTheme);
    localStorage.setItem('teacherTheme', newTheme);
  };

  const handleSectionChange = (section) => {
    // Prevent rapid navigation calls (throttling protection)
    const now = Date.now();
    let targetPath = '';
    
    // Determine target path based on section and portal
    switch (section) {
      case 'learn':
        if (portal === 'student') targetPath = '/student/learn';
        else if (portal === 'teacher') targetPath = '/teacher/dashboard';
        else if (portal === 'admin') targetPath = '/admin/dashboard';
        break;
      case 'chat':
        targetPath = '/student/chat';
        break;
      case 'curiosity':
        targetPath = '/student/curiosity';
        break;
      case 'quests':
        targetPath = '/student/quests';
        break;
      case 'leaderboard':
        targetPath = '/student/achievements/leaderboard';
        break;
      case 'shop':
        targetPath = '/student/achievements/shop';
        break;
      case 'profile':
        if (portal === 'student') targetPath = '/student/profile';
        else if (portal === 'teacher') targetPath = '/teacher/profile';
        else if (portal === 'admin') targetPath = '/admin/profile';
        break;
      case 'more':
        targetPath = '/student/learn';
        break;
      case 'community':
        if (portal === 'guardian') {
          targetPath = '/guardian/community';
        } else if (portal === 'admin') {
          targetPath = '/admin/community';
        } else if (portal === 'teacher') {
          targetPath = '/teacher/community';
        } else {
          targetPath = '/student/community';
        }
        break;
      case 'settings':
        targetPath = '/student/settings';
        break;
      case 'about':
        if (portal === 'student') targetPath = '/student/about';
        else if (portal === 'teacher') targetPath = '/teacher/about';
        else if (portal === 'admin') targetPath = '/admin/about';
        else if (portal === 'guardian') targetPath = '/guardian/about';
        break;

      default:
        if (portal === 'student') targetPath = '/student/learn';
        else if (portal === 'teacher') targetPath = '/teacher/dashboard';
        else if (portal === 'admin') targetPath = '/admin/dashboard';
    }
    
    // Prevent navigation if:
    // 1. Already on the target path
    // 2. Same navigation was called within last 300ms
    if (targetPath && targetPath !== location.pathname) {
      const lastNav = lastNavigationRef.current;
      if (lastNav.path === targetPath && (now - lastNav.time) < 300) {
        return; // Skip rapid duplicate navigation
      }
      
      lastNavigationRef.current = { path: targetPath, time: now };
      navigate(targetPath);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
    setIsSidebarOpen(false); // Close sidebar when logout modal opens
  };

  const handleLogoutConfirmed = () => {
    try {
      const currentPortal = getPortalType(window.location.pathname || '') || 'student';
      clearAuthTokens(currentPortal);
      clearAllSessions();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setShowLogoutModal(false);
      navigate('/login');
    }
  };

  // Render appropriate sidebar based on portal
  const renderSidebar = (onNavigate = () => {}, showCloseButton = false, onClose = () => {}, onLogout = () => {}) => {
    if (portal === 'teacher') {
      return <TeacherSidebar activeSection={currentActiveSection} setActiveSection={handleSectionChange} onNavigate={onNavigate} showCloseButton={showCloseButton} onClose={onClose} />;
    } else if (portal === 'admin') {
      return <AdminSidebar activeSection={currentActiveSection} setActiveSection={handleSectionChange} onNavigate={onNavigate} showCloseButton={showCloseButton} onClose={onClose} onLogout={onLogout} />;
    } else if (portal === 'guardian') {
      return <GuardianSidebar activeSection={currentActiveSection} setActiveSection={handleSectionChange} onNavigate={onNavigate} showCloseButton={showCloseButton} onClose={onClose} onLogout={onLogout} />;
    } else {
      return <Sidebar activeSection={activeSection} setActiveSection={handleSectionChange} onNavigate={onNavigate} showCloseButton={showCloseButton} onClose={onClose} onLogout={onLogout} />;
    }
  };

  // Render Teacher Portal with TeacherProvider
  if (portal === 'teacher') {
    const isDark = teacherTheme === 'dark';
    return (
      <TeacherProvider initialTheme={teacherTheme} onToggleTheme={handleToggleTheme}>
        <div className={cn("flex h-screen overflow-hidden", isDark ? "bg-[hsl(var(--main-bg))]" : "bg-[#FAF8F5]")}>
          <div className="hidden lg:block">
            <TeacherSidebar activeSection={currentActiveSection} setActiveSection={handleSectionChange} />
          </div>

          <Sheet open={isTeacherSidebarOpen} onOpenChange={setIsTeacherSidebarOpen}>
            <SheetContent
              side="left"
              className="p-0 w-80 max-w-[85vw]"
            >
              <TeacherSidebar
                activeSection={currentActiveSection}
                setActiveSection={handleSectionChange}
                className="w-full border-r-0"
                onNavigate={() => setIsTeacherSidebarOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <TeacherHeader onOpenSidebar={() => setIsTeacherSidebarOpen(true)} />
            {children}
          </div>
        </div>
      </TeacherProvider>
    );
  }

  // Render Student/Admin Portals normally
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Page timing for curiosity pages
  const pageStartRef = useRef(null);

  useEffect(() => {
    // Track pageview on route change
    try {
      trackPageView(location.pathname);
    } catch (e) {}

    // Page time tracking: start timer when entering curiosity (student)
    // and for teacher/admin/guardian portals; send when leaving those areas
    try {
      const pathname = location.pathname || '';
      const isStudentCuriosity = pathname.startsWith('/student/curiosity');
      const isTeacherAdminGuardian = portal === 'teacher' || portal === 'admin' || portal === 'guardian';
      const shouldTrack = isStudentCuriosity || isTeacherAdminGuardian;

      if (shouldTrack) {
        // start timer if not already started
        if (!pageStartRef.current) pageStartRef.current = Date.now();
      } else {
        // leaving tracked area
        if (pageStartRef.current) {
          const timeSpent = Math.round((Date.now() - pageStartRef.current) / 1000);
          trackEvent('page_time_spent', attachUserProps({ page: pathname, time_spent_seconds: timeSpent }));
          pageStartRef.current = null;
        }
      }
    } catch (e) {}
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-[hsl(var(--main-bg))] overflow-hidden">
      {/* Persistent Sidebar on large screens */}
      <div className="hidden lg:block">
        {renderSidebar(() => {}, false, () => {}, handleLogout)}
      </div>

      {/* Floating Sidebar Overlay when opened (only for small screens) */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-[9999] flex lg:hidden">
          <div className="w-4/5 max-w-sm h-screen shadow-lg relative z-50">
            {renderSidebar(() => setIsSidebarOpen(false), true, () => setIsSidebarOpen(false), handleLogout)}
          </div>
          <div className="flex-1 bg-transparent" onClick={() => setIsSidebarOpen(false)} />
        </div>
      )}

      {/* Open button when closed (hidden on large screens) */}
      {!isSidebarOpen && (
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="fixed top-3 left-3 z-[9999] p-1.5 rounded-full bg-white/75 opacity-75 hover:opacity-100 hover:bg-white transition-opacity duration-200 shadow-md lg:hidden"
          title="Open sidebar"
        >
          <Menu className="w-4 h-4 text-gray-700" />
        </button>
      )}

      {/* Main Content Area (disable overlay/blur on large screens)") */}
      <div className={cn(
        'flex-1 flex flex-col overflow-hidden transition-all duration-300',
        isSidebarOpen ? 'lg:filter blur-xl lg:opacity-30 lg:pointer-events-none' : '',
        portal === 'student' ? 'pb-24 lg:pb-0' : '' // Add bottom padding for mobile bottom nav
      )}>
        {children}
      </div>

      {/* Mobile Bottom Navigation for Student Portal */}
      {portal === 'student' && (
        <div className="lg:hidden">
          <MobileBottomNav
            activeSection={currentActiveSection}
            onSectionChange={handleSectionChange}
            onNavigate={() => setIsSidebarOpen(false)}
          />
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        title="Log out"
        description={`You will be signed out of the ${portal} portal and returned to login.`}
        confirmText="Log out"
        cancelText="Stay logged in"
        onConfirm={handleLogoutConfirmed}
        onCancel={() => setShowLogoutModal(false)}
        zIndex="z-[10000]"
      />
    </div>
  );
}
